import React, { useCallback, useEffect, useState } from 'react';
import { apiErrorText } from '../../utils/apiErrorText';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { CalendarCheck } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Checkbox } from '../ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '../ui/dialog';
import usePermissions from '../../hooks/usePermissions';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const prevMonth = () => {
  const d = new Date();
  d.setDate(1); d.setMonth(d.getMonth() - 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};

const PayoutRow = ({ row, checked, onCheck, onDate, canEdit }) => {
  const [val, setVal] = useState(row.paid_date || '');
  useEffect(() => setVal(row.paid_date || ''), [row.paid_date]);
  const commit = () => {
    if (val === (row.paid_date || '')) return;
    if (val && !/^(19|20)\d{2}-\d{2}-\d{2}$/.test(val)) { setVal(row.paid_date || ''); return; }
    onDate(val || null);
  };
  return (
    <tr className="border-b" data-testid={`wage-payout-row-${row.employee_id}`}>
      <td className="px-2 py-1">{canEdit && <Checkbox checked={checked} onCheckedChange={(v) => onCheck(!!v)} data-testid={`wage-payout-select-${row.employee_id}`} />}</td>
      <td className="px-2 py-1 text-sm">{row.name}</td>
      <td className="px-2 py-1 text-sm tabular-nums text-right">{String(row.hours).replace('.', ',')}</td>
      <td className="px-2 py-1">
        <Input type="date" className="h-8 w-full min-w-[8.5rem] sm:w-40 text-xs" value={val} disabled={!canEdit}
          onChange={(e) => setVal(e.target.value)} onBlur={commit} onKeyDown={(e) => e.key === 'Enter' && commit()}
          data-testid={`wage-payout-date-${row.employee_id}`} />
      </td>
    </tr>
  );
};

/** Accounts record when each employee's wages for a month were paid; the cost workbook uses it for NBP rates. */
export const WagePayoutsDialog = ({ open: openProp, onOpenChange, initialMonth, hideTrigger = false }) => {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const canEdit = can('budget.edit');
  const [openState, setOpenState] = useState(false);
  const open = openProp ?? openState;
  const setOpen = (v) => { setOpenState(v); onOpenChange?.(v); };
  const [month, setMonth] = useState(initialMonth || prevMonth());
  useEffect(() => { if (openProp && initialMonth) setMonth(initialMonth); }, [openProp, initialMonth]);
  const [rows, setRows] = useState([]);
  const [selected, setSelected] = useState([]);
  const [groupDate, setGroupDate] = useState('');
  const load = useCallback(async () => {
    try { const r = await axios.get(`${API}/wage-payouts`, { params: { month } }); setRows(r.data.rows); setSelected([]); }
    catch (e) { toast.error(apiErrorText(e, t, 'wagePayouts.failed')); }
  }, [month, t]);
  useEffect(() => { if (open && /^\d{4}-\d{2}$/.test(month)) load(); }, [open, month, load]);
  const save = async (ids, paid_date) => {
    try {
      await axios.put(`${API}/wage-payouts`, { month, employee_ids: ids, paid_date });
      setRows((rs) => rs.map((r) => (ids.includes(r.employee_id) ? { ...r, paid_date } : r)));
      toast.success(t('wagePayouts.saved', { count: ids.length }));
    } catch (e) { toast.error(apiErrorText(e, t, 'wagePayouts.failed')); }
  };
  const all = rows.length > 0 && selected.length === rows.length;
  return (
    <>
      {!hideTrigger && (
        <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setOpen(true)} data-testid="wage-payouts-open">
          <CalendarCheck className="h-3.5 w-3.5 mr-1" />{t('wagePayouts.button')}
        </Button>
      )}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="wage-payouts-dialog">
          <DialogHeader>
            <DialogTitle>{t('wagePayouts.title')}</DialogTitle>
            <DialogDescription>{t('wagePayouts.hint')}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap items-end gap-3">
            <div><Label className="text-xs">{t('wagePayouts.month')}</Label>
              <Input type="month" className="h-8 w-40" value={month} onChange={(e) => setMonth(e.target.value)} data-testid="wage-payouts-month" /></div>
            {canEdit && (
              <div className="flex flex-wrap items-end gap-2">
                <div><Label className="text-xs">{t('wagePayouts.groupDate')}</Label>
                  <Input type="date" className="h-8 w-40" value={groupDate} onChange={(e) => setGroupDate(e.target.value)} data-testid="wage-payouts-group-date" /></div>
                <Button size="sm" className="h-8" disabled={!selected.length || !groupDate} onClick={() => save(selected, groupDate)} data-testid="wage-payouts-apply">
                  {t('wagePayouts.apply', { count: selected.length })}</Button>
                <Button size="sm" variant="outline" className="h-8" disabled={!selected.length} onClick={() => save(selected, null)} data-testid="wage-payouts-clear">
                  {t('wagePayouts.clear')}</Button>
              </div>
            )}
          </div>
          {rows.length === 0 ? <p className="py-6 text-center text-sm text-slate-500" data-testid="wage-payouts-empty">{t('wagePayouts.empty')}</p> : (
            <div className="overflow-x-auto rounded border">
              <table className="w-full text-left">
                <thead className="bg-slate-50 text-[11px] text-slate-600"><tr>
                  <th className="px-2 py-2">{canEdit && <Checkbox checked={all} onCheckedChange={(v) => setSelected(v ? rows.map((r) => r.employee_id) : [])} data-testid="wage-payouts-select-all" />}</th>
                  <th className="px-2">{t('wagePayouts.employee')}</th><th className="px-2 text-right">{t('wagePayouts.hours')}</th><th className="px-2">{t('wagePayouts.paidOn')}</th>
                </tr></thead>
                <tbody>{rows.map((r) => (
                  <PayoutRow key={r.employee_id} row={r} canEdit={canEdit} checked={selected.includes(r.employee_id)}
                    onCheck={(v) => setSelected((p) => (v ? [...p, r.employee_id] : p.filter((x) => x !== r.employee_id)))}
                    onDate={(d) => save([r.employee_id], d)} />
                ))}</tbody>
              </table>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export { prevMonth };
export default WagePayoutsDialog;
